import { useToast } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { CreateCouponInput, useCreateCoupon } from '../hooks/useCreateCoupon';

const PAYABLE_TYPES = [
    { value: 'COURT_BOOKING', label: translate('coupon.applicable_court_booking') },
    { value: 'GAME_JOINING', label: translate('coupon.applicable_game_joining') },
    { value: 'EVENT_JOINING', label: translate('coupon.applicable_event_joining') },
    { value: 'COACHING_SESSION', label: translate('coupon.applicable_coaching_session') },
];

const defaultValues: CreateCouponInput = {
    name: '',
    discountType: 'PERCENTAGE',
    discountValue: 0,
    currency: undefined,
    applicableTo: ['COURT_BOOKING'],
    expiresAt: '',
    maxUsagePerUser: 1,
};

interface CreateCouponModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function CreateCouponModal({ isOpen, onClose }: CreateCouponModalProps) {
    const { mutateAsync: createCoupon, isLoading } = useCreateCoupon();
    const [formData, setFormData] = useState<CreateCouponInput>(defaultValues);
    const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
    const toast = useToast();

    useEffect(() => {
        if (isOpen) {
            setFormData(defaultValues);
            setErrors({});
        }
    }, [isOpen]);

    const handleInputChange = (field: keyof CreateCouponInput, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) {
            newErrors.name = translate('coupon.name_required');
        }
        if (!formData.discountValue || formData.discountValue <= 0) {
            newErrors.discountValue = translate('coupon.discount_value_required');
        }
        if (formData.discountType === 'PERCENTAGE' && formData.discountValue > 100) {
            newErrors.discountValue = translate('coupon.discount_value_max_percentage');
        }
        if (formData.discountType === 'FIXED' && !formData.currency) {
            newErrors.currency = translate('coupon.currency_required');
        }
        if (!formData.expiresAt) {
            newErrors.expiresAt = translate('coupon.expiry_required');
        }
        if (formData.applicableTo.length === 0) {
            newErrors.applicableTo = translate('coupon.applicable_to_required');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        try {
            await createCoupon({
                ...formData,
                expiresAt: new Date(formData.expiresAt).toISOString(),
            });
            toast({
                title: translate('coupon.created_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onClose();
        } catch (error: any) {
            toast({
                title: translate('coupon.create_error'),
                description: error.message || '',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={onClose}
            title={translate('coupon.create_coupon')}
            onSubmit={handleSubmit}
            submitLabel={translate('coupon.create')}
            cancelLabel={translate('common.cancel')}
            isSubmitting={isLoading}
            size="xl"
        >
            <FormStack>
                <FormField
                    type="text"
                    label={translate('coupon.name')}
                    value={formData.name}
                    onChange={(v) => handleInputChange('name', v)}
                    placeholder={translate('coupon.name_placeholder')}
                    error={errors.name}
                    isRequired
                />

                <FormField
                    type="select"
                    label={translate('coupon.discount_type')}
                    value={formData.discountType}
                    onChange={(v) => handleInputChange('discountType', v)}
                    options={[
                        { value: 'PERCENTAGE', label: translate('coupon.discount_type_percentage') },
                        { value: 'FIXED', label: translate('coupon.discount_type_fixed') },
                    ]}
                />

                <FormField
                    type="number"
                    label={
                        formData.discountType === 'PERCENTAGE'
                            ? translate('coupon.discount_value_percentage')
                            : translate('coupon.discount_value')
                    }
                    value={formData.discountValue}
                    onChange={(v) => handleInputChange('discountValue', v)}
                    min={0}
                    max={formData.discountType === 'PERCENTAGE' ? 100 : undefined}
                    error={errors.discountValue}
                    isRequired
                />

                {formData.discountType === 'FIXED' && (
                    <FormField
                        type="select"
                        label={translate('coupon.currency')}
                        value={formData.currency || ''}
                        onChange={(v) => handleInputChange('currency', v)}
                        placeholder={translate('coupon.select_currency')}
                        options={[
                            { value: 'eur', label: 'EUR' },
                            { value: 'usd', label: 'USD' },
                        ]}
                        error={errors.currency}
                        isRequired
                    />
                )}

                <FormField
                    type="checkbox-group"
                    label={translate('coupon.applicable_to')}
                    value={formData.applicableTo}
                    onChange={(v) => handleInputChange('applicableTo', v)}
                    options={PAYABLE_TYPES}
                    error={errors.applicableTo}
                    isRequired
                />

                <FormField
                    type="datetime-local"
                    label={translate('coupon.expires_at')}
                    value={formData.expiresAt}
                    onChange={(v) => handleInputChange('expiresAt', v)}
                    error={errors.expiresAt}
                    isRequired
                />

                <FormField
                    type="number"
                    label={translate('coupon.max_usage_per_user')}
                    value={formData.maxUsagePerUser}
                    onChange={(v) => handleInputChange('maxUsagePerUser', v)}
                    min={1}
                />
            </FormStack>
        </FormModal>
    );
}
